import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PageShell } from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, BadgeCheck, MapPin, CheckCircle2, AlertCircle, ArrowRight, Loader2, ExternalLink } from "lucide-react";

const STATE_LABEL = {
  DRAFT: "Draft", SUBMITTED: "Pending activation", UNDER_REVIEW: "Under review",
  APPROVED: "Approved", SCHEDULED: "Scheduled", ACTIVE: "Active",
  COMPLETED: "Completed", CANCELLED: "Cancelled", REJECTED: "Rejected", SYSTEM_PAUSED: "Paused",
};

function PreviewCard({ e }) {
  if (!e) return null;
  return (
    <div data-testid="promote-preview-card" className="rounded-2xl border-2 border-[#C79A3B]/40 bg-gradient-to-br from-[#0F2C59] to-[#173a6e] p-4 text-white">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-[#C79A3B]" />
        <span className="text-[11px] font-bold uppercase tracking-wide">PropertyConnect Spotlight · Featured Professional</span>
        <span className="ml-auto text-[10px] font-semibold uppercase text-white/50 border border-white/20 rounded-full px-2 py-0.5">Sponsored</span>
      </div>
      <div className="flex items-start gap-3">
        {e.photo ? <img src={e.photo} alt="" className="h-14 w-14 rounded-xl object-cover ring-2 ring-[#C79A3B]/50" />
          : <div className="h-14 w-14 rounded-xl bg-white/10 flex items-center justify-center text-lg font-bold">{(e.name || "?")[0]}</div>}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5"><span className="font-semibold truncate">{e.name}</span>{e.is_verified && <BadgeCheck className="h-4 w-4 text-[#C79A3B]" />}</div>
          {e.role_label && <div className="text-[13px] text-white/80 truncate">{e.role_label}</div>}
          {(e.city || e.province) && <div className="mt-0.5 flex items-center gap-1 text-[12px] text-white/60"><MapPin className="h-3 w-3" /> {[e.city, e.province].filter(Boolean).join(", ")}</div>}
        </div>
      </div>
      {(e.specialties || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">{e.specialties.map((s) => <span key={s} className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-medium">{s}</span>)}</div>
      )}
      <div className="mt-3.5 w-full rounded-xl bg-[#C79A3B] px-4 py-2 text-center text-sm font-semibold text-[#0F2C59]">View Profile</div>
    </div>
  );
}

export default function PromoteProfile() {
  const nav = useNavigate();
  const [opt, setOpt] = useState(undefined);
  const [objective, setObjective] = useState("");
  const [pkg, setPkg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);

  const loadMine = () => api.get("/campaigns").then(({ data }) =>
    setMine((data?.campaigns || data || []).filter((c) => (c.product || "") === "FEATURED_PROFESSIONAL"))).catch(() => {});

  useEffect(() => {
    api.get("/campaigns/featured-professional/options")
      .then(({ data }) => { setOpt(data); setObjective(data.objectives?.[0]?.key || ""); setPkg(data.packages?.[0]?.package_code || ""); })
      .catch((e) => { setOpt(null); toast.error(apiError(e)); });
    loadMine();
  }, []);

  const submit = async () => {
    if (!objective || !pkg) { toast.error("Choose a goal and a duration."); return; }
    setSubmitting(true);
    try {
      const { data: c } = await api.post("/campaigns", {
        placement: "CONNECTIONS_PROFESSIONAL_SPOTLIGHT", product: "FEATURED_PROFESSIONAL",
        entity_type: "PROFESSIONAL", entity_id: "self", package_code: pkg, objective,
        start_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
      });
      await api.post(`/campaigns/${c.id}/submit`);
      toast.success("Submitted! Your Featured Professional campaign will activate once payment is confirmed.");
      loadMine();
    } catch (e) { toast.error(apiError(e)); }
    setSubmitting(false);
  };

  const cancel = async (cid) => {
    try { await api.post(`/campaigns/${cid}/cancel`); toast.success("Campaign cancelled."); loadMine(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (opt === undefined) return <PageShell testid="promote-loading"><div className="flex justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div></PageShell>;

  return (
    <PageShell testid="promote-profile">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-1"><Sparkles className="h-5 w-5 text-[#C79A3B]" /><h1 className="text-2xl font-bold text-[#0F2C59]">Promote My Profile</h1></div>
        <p className="text-sm text-slate-500 mb-6">Get featured at the top of Connections so more of the network discovers your public profile. Featured is <b>paid visibility</b> — it never changes your verification, ranking or credentials.</p>

        {opt && !opt.enabled && (
          <div data-testid="promote-disabled" className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-800 text-sm">Featured Professional isn't available yet. Please check back soon.</div>
        )}

        {opt && opt.enabled && !opt.eligibility?.eligible && (
          <div data-testid="promote-ineligible" className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <div className="flex items-center gap-2 font-semibold text-amber-800 mb-2"><AlertCircle className="h-5 w-5" /> A few steps before you can promote</div>
            <ul className="space-y-1.5">
              {opt.eligibility.reasons.map((r) => <li key={r} className="flex items-center gap-2 text-sm text-amber-800"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> {r}</li>)}
            </ul>
            <Link to="/my-card" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F2C59]">Update my profile <ArrowRight className="h-4 w-4" /></Link>
          </div>
        )}

        {opt && opt.enabled && opt.eligibility?.eligible && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-[#0F2C59] mb-1.5">1. Choose your goal</label>
                <Select value={objective} onValueChange={setObjective}>
                  <SelectTrigger data-testid="promote-objective"><SelectValue placeholder="Select a goal" /></SelectTrigger>
                  <SelectContent>{opt.objectives.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0F2C59] mb-1.5">2. Choose duration</label>
                <div className="grid grid-cols-3 gap-2" data-testid="promote-packages">
                  {opt.packages.length === 0 && <p className="col-span-3 text-sm text-slate-500">Pricing is being finalized — check back soon.</p>}
                  {opt.packages.map((p) => (
                    <button key={p.package_code} data-testid={`promote-package-${p.duration_days}`} onClick={() => setPkg(p.package_code)}
                      className={`rounded-xl border-2 p-3 text-center transition-all ${pkg === p.package_code ? "border-[#0F2C59] bg-[#0F2C59]/5" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className="text-lg font-bold text-[#0F2C59]">{p.duration_days}d</div>
                      <div className="text-xs text-slate-500">₱{p.price_php.toLocaleString()}</div>
                    </button>
                  ))}
                </div>
              </div>
              <Button data-testid="promote-submit" onClick={submit} disabled={submitting || !opt.packages.length}
                className="w-full rounded-xl bg-[#0F2C59] hover:bg-[#173a6e]">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for activation"}
              </Button>
              <p className="text-[11px] text-slate-400">Submitting reserves your spot. Your campaign activates after payment is confirmed. You can cancel a pending campaign anytime.</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#0F2C59] mb-1.5">Preview</label>
              <PreviewCard e={opt.preview} />
              {opt.profile_path && <a href={opt.profile_path} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500">This links to your profile {opt.profile_path} <ExternalLink className="h-3 w-3" /></a>}
            </div>
          </div>
        )}

        {mine.length > 0 && (
          <div className="mt-10">
            <h2 className="text-lg font-semibold text-[#0F2C59] mb-3">My Featured campaigns</h2>
            <div className="space-y-2" data-testid="promote-my-campaigns">
              {mine.map((c) => (
                <div key={c.id} data-testid={`promote-campaign-${c.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#0F2C59]">{c.duration_days}-day Featured Professional</div>
                    <div className="text-xs text-slate-500">{c.objective ? c.objective.replaceAll("_", " ").toLowerCase() : ""} · starts {c.start_date}</div>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${["ACTIVE", "SCHEDULED", "APPROVED"].includes(c.lifecycle_state) ? "bg-green-100 text-green-700" : c.lifecycle_state === "SUBMITTED" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{STATE_LABEL[c.lifecycle_state] || c.lifecycle_state}</span>
                  {["DRAFT", "SUBMITTED"].includes(c.lifecycle_state) && (
                    <Button data-testid={`promote-cancel-${c.id}`} variant="ghost" size="sm" className="text-red-600" onClick={() => cancel(c.id)}>Cancel</Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}
